import 'react-day-picker/style.css';
import '~/themes/calendar.scss';

import { addDays, format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { DayPicker, useDayPicker, type MonthCaptionProps } from 'react-day-picker';
import { Box, Column, Row, Text, ZStack } from '~/commonComponents';
import Button from '~/commonComponents/Button';
import { Icon } from '~/commonComponents/Icon';
import { HeadRow } from '~/components/Request/multiday/components/Rows/DateRow/HeadRow';
import { Colors } from '~/themes/colors';

const ARROW_BUTTON_SIZE = 38;
const ARROW_BORDER_RADIUS = 6;
const ACTIVE_BOX_SHADOW = '0px 1px 2px rgba(0,0,0,0.15), 0px 3px 10px 1px rgba(0,0,0,0.15)';
const HOVER_BOX_SHADOW = '0px 2px 4px rgba(0,0,0,0.2), 0px 4px 14px 2px rgba(0,0,0,0.2)';

const arrowButtonStyle = (disabled: boolean) => ({
  all: 'unset' as const,
  display: 'flex',
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  width: ARROW_BUTTON_SIZE,
  height: ARROW_BUTTON_SIZE,
  borderRadius: ARROW_BORDER_RADIUS,
  backgroundColor: disabled ? Colors.secondary_200 : Colors.secondary_500,
  boxShadow: disabled ? 'none' : ACTIVE_BOX_SHADOW,
  cursor: disabled ? ('default' as const) : ('pointer' as const),
  pointerEvents: disabled ? ('none' as const) : ('auto' as const),
  transition: 'transform 0.2s ease',
});

const handleArrowHover = (e: React.MouseEvent<HTMLButtonElement>, disabled: boolean) => {
  if (!disabled) {
    e.currentTarget.style.transform = 'scale(1.1)';
    e.currentTarget.style.backgroundColor = Colors.secondary_350;
    e.currentTarget.style.boxShadow = HOVER_BOX_SHADOW;
  }
};

const handleArrowLeave = (e: React.MouseEvent<HTMLButtonElement>, disabled: boolean) => {
  e.currentTarget.style.transform = 'scale(1)';
  e.currentTarget.style.backgroundColor = disabled ? Colors.secondary_200 : Colors.secondary_500;
  e.currentTarget.style.boxShadow = disabled ? 'none' : ACTIVE_BOX_SHADOW;
};

function SingleMonthNavigation({ calendarMonth }: MonthCaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();
  const displayMonth = calendarMonth.date;
  const isPrevDisabled = !previousMonth;
  const isNextDisabled = !nextMonth;

  return (
    <Row alignItems="center" gap={10}>
      <button
        type="button"
        data-testid="month-nav-prev"
        data-cy="month-nav-prev"
        disabled={isPrevDisabled}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        style={arrowButtonStyle(isPrevDisabled)}
        onMouseEnter={(e) => handleArrowHover(e, isPrevDisabled)}
        onMouseLeave={(e) => handleArrowLeave(e, isPrevDisabled)}
      >
        <Icon name="chevron-left" color={Colors.white} />
      </button>

      <Text
        data-testid="month-nav-label"
        data-cy="month-nav-label"
        bold
        fontSize={22}
        textAlign="center"
        flex={1}
        backgroundColor={Colors.secondary_50}
        borderRadius={6}
        padding="4px 12px"
      >
        {format(displayMonth, 'MMMM')}
      </Text>

      <button
        type="button"
        data-testid="month-nav-next"
        data-cy="month-nav-next"
        disabled={isNextDisabled}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        style={arrowButtonStyle(isNextDisabled)}
        onMouseEnter={(e) => handleArrowHover(e, isNextDisabled)}
        onMouseLeave={(e) => handleArrowLeave(e, isNextDisabled)}
      >
        <Icon name="chevron-right" color={Colors.white} />
      </button>
    </Row>
  );
}

interface IDatePickerPopupProps {
  currentDate: Date;
  isOpen: boolean;
  onToggle: () => void;
  onConfirm: (date: Date) => void;
}

export const DatePickerPopup = ({
  currentDate,
  isOpen,
  onToggle,
  onConfirm,
}: IDatePickerPopupProps) => {
  const [pendingDate, setPendingDate] = useState<Date>(currentDate);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setPendingDate(currentDate);
    }
  }, [isOpen, currentDate]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onToggle();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  if (!isOpen) return null;

  const today = new Date();

  const handleConfirm = () => {
    onConfirm(pendingDate);
  };

  return (
    <Column
      ref={popupRef}
      data-testid="date-picker-popup"
      data-cy="date-picker-popup"
      backgroundColor={Colors.white}
      border={`1.5px solid ${Colors.neutral_100}`}
      borderRadius={12}
      alignItems="center"
      style={{
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.1)',
        position: 'absolute',
        top: '100%',
        left: '-12%',
        transform: 'translateX(-50%)',
        marginTop: 8,
        zIndex: 4,
        padding: '20px 36px',
      }}
    >
      <Row
        alignItems="center"
        justifyContent="center"
        marginBottom={8}
        marginTop={16}
        position="relative"
        width="100%"
        style={{ cursor: 'pointer' }}
        onClick={onToggle}
        data-testid="date-picker-header"
        data-cy="date-picker-header"
      >
        <Text bold fontSize={22} color="primary_500">
          Select Date
        </Text>
        <ZStack
          position="absolute"
          right={-16}
          top={-16}
          style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}
        >
          <Icon name="chevron-down" size={24} color={Colors.secondary_500} />
        </ZStack>
      </Row>

      <Box alignItems="center">
        <DayPicker
          data-testid="date-picker-calendar"
          data-cy="date-picker-calendar"
          mode="single"
          numberOfMonths={1}
          selected={pendingDate}
          onSelect={(date) => date && setPendingDate(date)}
          startMonth={today}
          components={{
            MonthCaption: SingleMonthNavigation,
            Weekdays: HeadRow,
          }}
          disabled={[
            {
              before: today,
              after: addDays(today, 99),
            },
          ]}
        />
      </Box>

      <Button
        text="Confirm"
        data-testid="date-picker-confirm"
        dataCy="date-picker-confirm"
        onClick={handleConfirm}
        style={{
          width: 152,
          height: 48,
          marginTop: 8,
          backgroundColor: Colors.secondary_500,
          borderRadius: 24,
          marginBottom: 16,
        }}
      />
    </Column>
  );
};
